import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { format, parseISO } from "date-fns";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import type { ApplyCloseTemplateEntitySelectionRow } from "../applyCloseTemplateEntitySelection.types";
import { reconcileWorkEndDate } from "../applyCloseTemplateWorkDates";
import { useTemplateApplication } from "../useTemplateApplication";
import styles from "../ApplyCloseTemplateDrawer.module.scss";

interface Props {
    row: CellContext<
        ViewTableFeatures,
        ApplyCloseTemplateEntitySelectionRow,
        string
    >["row"];
    field: "startDate" | "endDate" | "workStartDate" | "workDeliveryDate";
    addText: string;
    placeholder: string;
}

export const ApplyCloseEntityDateCell = ({
    row,
    field,
    addText,
    placeholder,
}: Props) => {
    const { getEntityRow, setEntityRowField } = useTemplateApplication();
    const item = row.original;

    if (item.isClientRow || !item.entityIdStr) {
        return null;
    }

    const idStr = item.entityIdStr;
    const rowState = getEntityRow(idStr);
    if (!row.original.isApplicationSelected || !rowState) {
        return (
            <ViewTableCell>
                <span className="text-muted">-</span>
            </ViewTableCell>
        );
    }

    const dateOnlyValue = rowState[field] ?? undefined;
    const formattedValue = dateOnlyValue
        ? format(parseISO(dateOnlyValue), fullDateFullYearFormat)
        : undefined;
    const minDate =
        field === "workDeliveryDate"
            ? (rowState.workStartDate ?? undefined)
            : undefined;

    return (
        <ViewTableCell>
            <div className={styles.dateCell}>
                <CaptureClicks>
                    <DatePicker
                        mode="day"
                        variant="panel-input"
                        clearable
                        showTriggerIcon={false}
                        displayFormat={fullDateFullYearFormat}
                        placeholder={placeholder}
                        value={dateOnlyValue}
                        minDate={minDate}
                        onValueChange={(value) => {
                            const nextValue = value ?? null;
                            setEntityRowField(idStr, field, nextValue);
                            if (field === "workStartDate") {
                                setEntityRowField(
                                    idStr,
                                    "workDeliveryDate",
                                    reconcileWorkEndDate(
                                        nextValue,
                                        rowState.workDeliveryDate,
                                    ),
                                );
                            }
                        }}
                        trigger={
                            <EditableCellContent
                                isEmpty={!dateOnlyValue}
                                addText={addText}
                                hideIcon
                            >
                                {formattedValue}
                            </EditableCellContent>
                        }
                    />
                </CaptureClicks>
            </div>
        </ViewTableCell>
    );
};
